package com.example.monetix.utils

import com.example.monetix.db.MonetixDatabase
import com.example.monetix.db.FirestoreHelper
import com.example.monetix.db.TransactionEntity
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import app.cash.sqldelight.async.coroutines.awaitAsList
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import kotlinx.datetime.Instant
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.plus
import kotlinx.datetime.DayOfWeek
import kotlinx.coroutines.launch

object AutomationEngine {

    private val mesiItaliani = listOf("Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre")

    private data class SmartData(val matchKeys: List<String>, val domain: String? = null, val emoji: String? = null, val customUrl: String? = null)

    private val smartMerchants = listOf(
        SmartData(listOf("lidl", "eurospin", "conad", "coop", "esselunga", "pam", "carrefour", "md", "aldi", "crai", "despar", "famila", "iper", "tigros"), emoji = "🛒"),
        SmartData(listOf("macelleria", "panificio", "fruttivendolo", "pescheria"), emoji = "🥩"),
        SmartData(listOf("acqua e sapone", "tigota", "tigotà", "cad", "risparmio casa"), emoji = "🧼"),
        SmartData(listOf("ikea", "leroy merlin", "brico", "obi", "tecnomat", "maisons du monde"), emoji = "🛋️"),
        SmartData(listOf("enel", "eni", "a2a", "iren", "hera", "edison", "sorgenia", "illumia", "plenitude", "nenn", "octopus"), emoji = "⚡"),
        SmartData(listOf("tim", "vodafone", "wind", "iliad", "fastweb", "ho mobile", "very mobile", "kena"), emoji = "📱"),
        SmartData(listOf("tari", "tasi", "imu", "f24", "agenzia entrate", "pago pa", "pagopa", "bollo"), emoji = "🏛️"),
        SmartData(listOf("q8", "eni station", "ip", "tamoil", "esso", "distributore", "benzina", "carburante", "rifornimento"), emoji = "⛽"),
        SmartData(listOf("telepass", "unipolmove", "autostrade", "pedaggio"), emoji = "🛣️"),
        SmartData(listOf("trenitalia", "italo", "atm", "atac", "cotral", "ryanair", "easyjet", "wizzair", "ita airways", "biglietto"), emoji = "🚆"),
        SmartData(listOf("uber", "bolt", "freenow", "taxi"), emoji = "🚕"),
        SmartData(listOf("amazon", "amzn", "prime"), domain = "amazon.it"),
        SmartData(listOf("shein", "temu", "aliexpress", "zalando", "ebay"), emoji = "📦"),
        SmartData(listOf("zara", "vinted", "asos", "h&m", "nike", "adidas", "primark", "ovs", "intimissimi", "calzedonia"), emoji = "👕"),
        SmartData(listOf("netflix", "disney", "prime video", "now tv", "dazn", "infinity"), domain = "netflix.com"),
        SmartData(listOf("spotify", "apple music", "youtube premium"), emoji = "🎵"),
        SmartData(listOf("playstation", "xbox", "nintendo", "steam", "epic games", "videogioco"), emoji = "🎮"),
        SmartData(listOf("mcdonalds", "mc donald", "burger king", "kfc", "roadhouse", "old wild west", "dominos"), emoji = "🍔"),
        SmartData(listOf("deliveroo", "just eat", "glovo", "uber eats"), emoji = "🛵"),
        SmartData(listOf("bar", "pasticceria", "caffetteria", "starbucks", "colazione", "caffè", "cornetto"), emoji = "☕"),
        SmartData(listOf("pizzeria", "ristorante", "trattoria", "osteria", "cena", "pranzo", "sushi", "pokè"), emoji = "🍽️"),
        SmartData(listOf("farmacia", "parafarmacia", "farmacosmo"), emoji = "💊"),
        SmartData(listOf("dentista", "medico", "visita", "ospedale", "asl", "ticket", "esami"), emoji = "⚕️"),
        SmartData(listOf("findomestic"), "findomestic.it"),
        SmartData(listOf("compass"), "compass.it"),
        SmartData(listOf("agos"), "agos.it"),
        SmartData(listOf("younited"), "younited-credit.com"),
        SmartData(listOf("cofidis"), "cofidis.it"),
        SmartData(listOf("santander"), "santanderconsumer.it"),
        SmartData(listOf("fiditalia"), "fiditalia.it"),
        SmartData(listOf("prestipay"), "prestipay.it"),
        SmartData(listOf("ca auto bank"), "ca-autobank.it"),
        SmartData(listOf("sella personal credit"), "sellapersonalcredit.it"),
        SmartData(listOf("intesa sanpaolo"), "intesasanpaolo.com"),
        SmartData(listOf("unicredit"), "unicredit.it"),
        SmartData(listOf("bnl"), "bnl.it"),
        SmartData(listOf("poste italiane", "libretto postale", "postepay"), "poste.it"),
        SmartData(listOf("paypal"), customUrl = "https://logo.clearbit.com/paypal.com"),
        SmartData(listOf("klarna"), customUrl = "https://logo.clearbit.com/klarna.com"),
        SmartData(listOf("scalapay"), "scalapay.com"),
        SmartData(listOf("clearpay"), "clearpay.com"),
        SmartData(listOf("volkswagen"), "vwfs.it")
    )

    private fun getSmartLogo(name: String): Pair<String?, String?> {
        val cleanName = " ${name.lowercase()} ".replace(Regex("[^a-z0-9]"), " ")
        val match = smartMerchants.firstOrNull { m -> m.matchKeys.any { key -> cleanName.contains(" $key ") } }
        val emojiOut = match?.emoji
        val uriOut = match?.customUrl ?: match?.domain?.let { "https://icon.horse/icon/$it" } ?: if (emojiOut == null) autoFaviconUrlForName(name) else null
        return Pair(emojiOut, uriOut)
    }

    // --- Funzione Helper per il Favicon (sostituiscila se ne hai una tua) ---
    private fun autoFaviconUrlForName(name: String): String? {
        val n = name.trim().lowercase().replace(" ", "")
        if(n.isBlank()) return null
        return "https://logo.clearbit.com/$n.com"
    }

    private val processMutex = kotlinx.coroutines.sync.Mutex()

    suspend fun processPendingAutomations(db: MonetixDatabase, firestore: FirestoreHelper, forcedBalanceCents: Long? = null) = withContext(Dispatchers.Default) {
        if (!processMutex.tryLock()) return@withContext
        try {
            val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
            val currentEpochDay = today.toEpochDays().toLong()
            val nowMs = Clock.System.now().toEpochMilliseconds()

            val allTransactions = db.transactionQueries.getAllTransactions().awaitAsList()

            // 🚀 MOTORE SPESE RICORRENTI E ABBONAMENTI 🚀
            val allRecurring = db.recurringExpenseQueries.all().awaitAsList()
            val toProcess = allRecurring.filter { it.isEnabled && it.autoGenerateOnReminder && !it.isDeleted }

            for (expense in toProcess) {
                val lastUpdateDay = Instant.fromEpochMilliseconds(expense.updatedAtEpochMs).toLocalDateTime(TimeZone.currentSystemDefault()).date.toEpochDays().toLong()

                if (currentEpochDay >= expense.nextEpochDay && expense.nextEpochDay >= lastUpdateDay) {

                    val alreadyPaidEarly = allTransactions.any { tx ->
                        !tx.isDeleted &&
                                tx.dateEpochDay == expense.nextEpochDay &&
                                (tx.description?.contains("[rec:${expense.id}]") == true || tx.merchantKey?.contains("[rec:${expense.id}]") == true)
                    }

                    val cartellaRegex = Regex("\\[(?:CART|TAX):([^\\]]*)\\]", RegexOption.IGNORE_CASE)
                    val cartellaMatch = expense.notes?.let { cartellaRegex.find(it) }

                    var currentAmountCents = expense.amountCents
                    var updatedNotes = expense.notes
                    var nextAmountCents = expense.amountCents
                    var isCartellaFinished = false

                    if (cartellaMatch != null) {
                        val ratesStr = cartellaMatch.groupValues[1]
                        val rates = if (ratesStr.isNotBlank()) ratesStr.split(",").map { it.trim() }.toMutableList() else mutableListOf()
                        isCartellaFinished = true

                        for (i in rates.indices) {
                            if (!rates[i].endsWith(":P", ignoreCase = true) && !rates[i].endsWith(":X", ignoreCase = true)) {
                                currentAmountCents = rates[i].replace(":P", "").replace(":X", "").toLongOrNull() ?: expense.amountCents
                                rates[i] = rates[i] + ":P"

                                val nextIndex = i + 1
                                if (nextIndex < rates.size) {
                                    nextAmountCents = rates[nextIndex].replace(":P", "").toLongOrNull() ?: 0L
                                    isCartellaFinished = false
                                }
                                break
                            }
                        }
                        val newCartList = rates.joinToString(",")
                        val tagType = if (cartellaMatch.value.startsWith("[TAX", ignoreCase = true)) "TAX" else "CART"
                        updatedNotes = expense.notes!!.replace(cartellaMatch.value, "[$tagType:$newCartList]")
                    }

                    if (!alreadyPaidEarly) {
                        val actualToAccount = if (expense.toAccountId == "null" || expense.toAccountId.isNullOrBlank()) null else expense.toAccountId
                        val isTransfer = expense.type == "TRANSFER" || actualToAccount != null
                        val txType = if (isTransfer) "TRANSFER" else expense.type

                        val dateOfExpense = LocalDate.fromEpochDays(expense.nextEpochDay.toInt())
                        val txId = "auto_${expense.id}_${dateOfExpense.year}_${dateOfExpense.monthNumber}"

                        val cleanName = expense.name.trim()
                        val meseStr = mesiItaliani.getOrNull(dateOfExpense.monthNumber - 1) ?: ""

                        val visualNote = updatedNotes?.replace(Regex("\\[(?:CART|TAX):[^\\]]*\\]", RegexOption.IGNORE_CASE), "")?.trim()
                        val baseNote = visualNote.takeIf { !it.isNullOrBlank() } ?: cleanName

                        val recTag = "[rec:${expense.id}]"
                        val finalDescription = "$baseNote $meseStr $recTag".trim()
                        val finalMerchantKey = "$cleanName $recTag".trim()

                        var finalEmoji = expense.logoEmoji?.takeIf { it.isNotBlank() }
                        var finalUri = expense.logoUri?.takeIf { it.isNotBlank() }

                        if (finalEmoji == null && finalUri == null) {
                            val (smartEmoji, smartUri) = getSmartLogo(cleanName)
                            finalEmoji = smartEmoji
                            finalUri = smartUri
                        }

                        val subCategoryLabel = when(txType) {
                            "INCOME" -> "Entrata Automatica"
                            "TRANSFER" -> "Giroconto Automatico"
                            else -> if (cartellaMatch != null) "Cartella Esattoriale" else "Spesa Automatica"
                        }

                        val newTx = TransactionEntity(
                            id = txId, accountId = expense.accountId, toAccountId = if (isTransfer) actualToAccount else null,
                            fromAccountId = if (isTransfer) expense.accountId else null, amountCents = kotlin.math.abs(currentAmountCents),
                            type = txType, dateEpochDay = expense.nextEpochDay, categoryId = expense.categoryId,
                            description = finalDescription, merchantKey = finalMerchantKey, subcategoryText = subCategoryLabel,
                            logoEmoji = finalEmoji, logoUri = finalUri, isHidden = false, isDeleted = false, reminderDays = null, createdAtEpochMs = nowMs, updatedAtEpochMs = nowMs
                        )

                        db.transactionQueries.upsert(
                            newTx.id, newTx.accountId, newTx.toAccountId, newTx.fromAccountId, newTx.amountCents, newTx.type,
                            newTx.dateEpochDay, newTx.categoryId, newTx.description, newTx.merchantKey, newTx.subcategoryText,
                            newTx.logoEmoji, newTx.logoUri, newTx.isHidden, newTx.isDeleted, newTx.reminderDays, newTx.createdAtEpochMs, newTx.updatedAtEpochMs
                        )
                        launch { firestore.salva("movimenti", newTx.id, newTx) }
                    }

                    val dateOfExpenseForUpdate = LocalDate.fromEpochDays(expense.nextEpochDay.toInt())
                    val nextDate = calcolaProssimaScadenza(dateOfExpenseForUpdate, expense.dayOfMonth.toInt(), expense.frequency)
                    val finalNotes = if (isCartellaFinished) {
                        val cln = updatedNotes ?: ""
                        if (cln.contains("[ARCHIVED:true]")) cln else "$cln [ARCHIVED:true]"
                    } else { updatedNotes }

                    val updatedRec = expense.copy(
                        nextEpochDay = nextDate.toEpochDays().toLong(), updatedAtEpochMs = nowMs,
                        notes = finalNotes, amountCents = nextAmountCents, isEnabled = true
                    )

                    db.recurringExpenseQueries.upsert(
                        updatedRec.id, updatedRec.name, updatedRec.type, updatedRec.amountCents, updatedRec.accountId,
                        updatedRec.toAccountId, updatedRec.categoryId, updatedRec.dayOfMonth, updatedRec.isEnabled,
                        updatedRec.isDeleted, updatedRec.cycleTarget, updatedRec.logoEmoji, updatedRec.logoUri,
                        updatedRec.notes, updatedRec.autoGenerateOnReminder, updatedRec.nextEpochDay,
                        updatedRec.frequency, updatedRec.isPaused, updatedRec.startEpochDay,
                        updatedRec.createdAtEpochMs, updatedRec.updatedAtEpochMs
                    )
                    launch { firestore.salva("ricorrenti", updatedRec.id, updatedRec) }
                }
            }

            // 🚀 MOTORE FINANZIAMENTI 🚀
            val allFinancings = db.financingQueries.all().awaitAsList()
            val activeFinancings = allFinancings.filter { !it.isClosed && !it.isDeleted && it.autoGenerateOnReminder }

            for (fin in activeFinancings) {
                val start = LocalDate.fromEpochDays(fin.startEpochDay.toInt())
                val end = LocalDate.fromEpochDays(fin.endEpochDay.toInt())

                val match = Regex("\\[DAY:(\\d+)\\]").find(fin.purpose ?: "")
                val dueDay = match?.groupValues?.get(1)?.toIntOrNull() ?: start.dayOfMonth

                var currentDue = LocalDate(start.year, start.monthNumber, minOf(dueDay, lengthOfMonthKmp(start.year, start.monthNumber)))
                if (currentDue < start) {
                    currentDue = addMonths(currentDue, 1, dueDay)
                }

                val lastUpdateDay = Instant.fromEpochMilliseconds(fin.updatedAtEpochMs).toLocalDateTime(TimeZone.currentSystemDefault()).date.toEpochDays().toLong()

                while (currentDue <= today && currentDue <= end) {
                    val currentDueEpoch = currentDue.toEpochDays().toLong()
                    if (currentDueEpoch < lastUpdateDay) {
                        currentDue = addMonths(currentDue, 1, dueDay)
                        continue
                    }

                    val meseStr = mesiItaliani.getOrNull(currentDue.monthNumber - 1) ?: ""

                    val alreadyPaid = allTransactions.any { tx ->
                        val isCorrectFin = (tx.description?.contains("[fin:${fin.id}]") == true || tx.merchantKey?.contains("[fin:${fin.id}]") == true)
                        val isCorrectMonth = LocalDate.fromEpochDays(tx.dateEpochDay.toInt()).let { d -> d.year == currentDue.year && d.monthNumber == currentDue.monthNumber } ||
                                (tx.description?.contains(meseStr, ignoreCase = true) == true)
                        
                        !tx.isDeleted && tx.type == "EXPENSE" && tx.accountId == fin.accountId && isCorrectFin && isCorrectMonth
                    }

                    if (!alreadyPaid) {
                        var totalCents = fin.installmentAmountCents + (fin.commissionCents ?: 0L)
                        var descSuffix = ""
                        if ((fin.extraInstallmentCents ?: 0L) > 0L && fin.extraInstallmentMonth?.toInt() == currentDue.monthNumber) {
                            totalCents += fin.extraInstallmentCents!!; descSuffix = " + Rata Extra"
                        }
                        val txId = "auto_fin_${fin.id}_${currentDue.year}_${currentDue.monthNumber}"
                        val cleanCreditor = (fin.creditorName ?: fin.name).trim()
                        val baseNote = fin.purpose?.replace(Regex("\\[DAY:\\d+\\]"), "")?.trim()?.takeIf { it.isNotBlank() } ?: fin.name.trim()

                        val finalDescription = "$baseNote $meseStr$descSuffix [fin:${fin.id}]"
                        var finalEmoji = fin.logoEmoji?.takeIf { it.isNotBlank() }
                        var finalUri = fin.logoUri?.takeIf { it.isNotBlank() }

                        if (finalEmoji == null && finalUri == null) {
                            val (smartEmoji, smartUri) = getSmartLogo(cleanCreditor)
                            finalEmoji = smartEmoji; finalUri = smartUri
                        }

                        val firestoreTx = TransactionEntity(
                            txId, fin.accountId ?: "", null, null, totalCents, "EXPENSE", currentDue.toEpochDays().toLong(),
                            fin.categoryId ?: "", finalDescription, cleanCreditor, "Rata debito", finalEmoji, finalUri, false, false, reminderDays = null, nowMs, nowMs
                        )
                        db.transactionQueries.upsert(
                            firestoreTx.id, firestoreTx.accountId, firestoreTx.toAccountId, firestoreTx.fromAccountId, firestoreTx.amountCents, firestoreTx.type,
                            firestoreTx.dateEpochDay, firestoreTx.categoryId, firestoreTx.description, firestoreTx.merchantKey, firestoreTx.subcategoryText,
                            firestoreTx.logoEmoji, firestoreTx.logoUri, firestoreTx.isHidden, firestoreTx.isDeleted, firestoreTx.reminderDays, firestoreTx.createdAtEpochMs, firestoreTx.updatedAtEpochMs
                        )
                        launch { firestore.salva("movimenti", txId, firestoreTx) }
                    }
                    currentDue = addMonths(currentDue, 1, dueDay)
                }
            }

            // ==============================================================
            // 🚀 MOTORE SMART PAC (PIANO DI ACCUMULO CONDIZIONATO) 🚀
            // ==============================================================
            try {
                val allGoals = db.savingsGoalQueries.all().awaitAsList()
                val activePacs = allGoals.filter { !it.isDeleted && !it.isCompleted && it.name.contains("[PAC:") }

                if (activePacs.isNotEmpty()) {
                    val validAccountsList = db.accountQueries.getAllAccounts().awaitAsList().filter { !it.isDeleted && !it.isArchived && !it.id.startsWith("sys_") && it.id != "salvadanaio_virtuale" }
                    val primaryAccountId = validAccountsList.firstOrNull()?.id

                    if (primaryAccountId != null) {
                        // 1. PREPARIAMO LA MAPPA DEL SALDO DISPONIBILE (Copia ESATTA di BalanceCalculator / HomeScreen)
                        val balancesMap = mutableMapOf<String, Long>()
                        
                        // Per calcolare lo stipendio ci servono le impostazioni o i valori di default
                        val salaryDay = 27
                        val decemberSalaryDay = 20
                        val anticipateWeekends = true
                        
                        val currentMonthSalary = com.example.monetix.getSalaryDate(today.year, today.monthNumber, salaryDay, decemberSalaryDay, anticipateWeekends)
                        val payPeriodStart: LocalDate
                        val payPeriodEnd: LocalDate

                        if (today < currentMonthSalary) {
                            val prevMonthDate = addMonths(today, -1, today.dayOfMonth)
                            payPeriodStart = com.example.monetix.getSalaryDate(prevMonthDate.year, prevMonthDate.monthNumber, salaryDay, decemberSalaryDay, anticipateWeekends)
                            // 🚀 FIX MANCANTE: minus per togliere giorni si fa aggiungendo giorni negativi o con kotlinx.datetime
                            // In Kotlinx Datetime KMP si usa `plus(DatePeriod(days = -1))`
                            payPeriodEnd = currentMonthSalary.plus(DatePeriod(days = -1))
                        } else {
                            val nextMonthDate = addMonths(today, 1, today.dayOfMonth)
                            val nextMonthSalary = com.example.monetix.getSalaryDate(nextMonthDate.year, nextMonthDate.monthNumber, salaryDay, decemberSalaryDay, anticipateWeekends)
                            payPeriodStart = currentMonthSalary
                            payPeriodEnd = nextMonthSalary.plus(DatePeriod(days = -1))
                        }

                        val startOfMonthEpochDay = payPeriodStart.toEpochDays().toLong()
                        val endOfMonthEpochDay = payPeriodEnd.toEpochDays().toLong()

                        // A. Calcoliamo il saldo dello Scudo
                        val emergencyTxs = allTransactions.filter { !it.isDeleted && (it.accountId == "sys_emergency_fund" || it.toAccountId == "sys_emergency_fund" || it.fromAccountId == "sys_emergency_fund") }
                        var emergencyBalanceCents = 0L
                        for (tx in emergencyTxs) {
                            if (tx.dateEpochDay > currentEpochDay) continue
                            when (tx.type) {
                                "INCOME" -> if (tx.accountId == "sys_emergency_fund") emergencyBalanceCents += tx.amountCents
                                "EXPENSE" -> if (tx.accountId == "sys_emergency_fund") emergencyBalanceCents -= tx.amountCents
                                "TRANSFER" -> {
                                    if (tx.fromAccountId == "sys_emergency_fund") emergencyBalanceCents -= tx.amountCents
                                    if (tx.toAccountId == "sys_emergency_fund") emergencyBalanceCents += tx.amountCents
                                }
                            }
                        }

                        validAccountsList.forEach { acc ->
                            // B. Calcoliamo la Base Liquida del conto (ESCLUDENDO I FONDI NELLO SCUDO)
                            var baseAvailable = 0L
                            allTransactions.forEach { tx ->
                                if (!tx.isDeleted && tx.dateEpochDay <= endOfMonthEpochDay) {
                                    val isToSavingsAcc = tx.type == "TRANSFER" && tx.toAccountId != null && validAccountsList.any { it.id == tx.toAccountId && it.isSavings }
                                    val isFutureSavings = tx.dateEpochDay > currentEpochDay && (
                                        tx.merchantKey?.contains("[pb:", ignoreCase = true) == true ||
                                        tx.merchantKey?.contains("[sav:", ignoreCase = true) == true ||
                                        tx.merchantKey?.contains("[goal:", ignoreCase = true) == true ||
                                        tx.merchantKey?.contains("[pac_sav:", ignoreCase = true) == true ||
                                        tx.merchantKey?.contains("salvadanaio", ignoreCase = true) == true ||
                                        tx.description?.contains("[pb:", ignoreCase = true) == true ||
                                        tx.description?.contains("[sav:", ignoreCase = true) == true ||
                                        tx.description?.contains("[goal:", ignoreCase = true) == true ||
                                        tx.description?.contains("[pac_sav:", ignoreCase = true) == true ||
                                        tx.description?.contains("salvadanaio", ignoreCase = true) == true ||
                                        isToSavingsAcc
                                    )
                                    val isCash = tx.id.startsWith("tx_cash_") || tx.accountId?.startsWith("sys_cash_") == true || tx.merchantKey?.contains("[cash:", ignoreCase = true) == true || tx.description?.contains("[cash:", ignoreCase = true) == true
                                    
                                    // I movimenti FUTURI (savings) e i CONTANTI non concorrono al saldo liquido bancario
                                    if (!isFutureSavings && !isCash) {
                                        when (tx.type) {
                                            "INCOME" -> if (tx.accountId == acc.id && tx.dateEpochDay <= currentEpochDay) baseAvailable += tx.amountCents
                                            "EXPENSE" -> if (tx.accountId == acc.id) baseAvailable -= tx.amountCents
                                            "TRANSFER" -> {
                                                if (tx.fromAccountId == acc.id) baseAvailable -= tx.amountCents
                                                if (tx.toAccountId == acc.id && tx.dateEpochDay <= currentEpochDay) baseAvailable += tx.amountCents
                                            }
                                        }
                                    }
                                }
                            }

                            // C. Detraiamo le Scadenze e le Rate del mese
                            val unpaidRec = allRecurring.filter { rec ->
                                !rec.isDeleted && rec.isEnabled && rec.type != "INCOME" && rec.nextEpochDay <= endOfMonthEpochDay &&
                                (rec.accountId == acc.id || (rec.accountId.isBlank() && acc.id == primaryAccountId))
                            }.sumOf { rec ->
                                val hasPaidThisMonth = allTransactions.any { tx ->
                                    !tx.isDeleted && tx.dateEpochDay >= startOfMonthEpochDay &&
                                    (tx.description?.contains("[rec:${rec.id}]", ignoreCase = true) == true || tx.merchantKey?.contains("[rec:${rec.id}]", ignoreCase = true) == true)
                                }
                                if (!hasPaidThisMonth) rec.amountCents else 0L
                            }

                            val unpaidFin = allFinancings.filter { fin ->
                                !fin.isClosed && !fin.isDeleted && (fin.accountId == acc.id || (fin.accountId.isNullOrBlank() && acc.id == primaryAccountId))
                            }.sumOf { fin ->
                                val hasPaidThisMonth = allTransactions.any { tx ->
                                    !tx.isDeleted && tx.dateEpochDay >= startOfMonthEpochDay &&
                                    (tx.description?.contains("[fin:${fin.id}]", ignoreCase = true) == true || tx.merchantKey?.contains("[fin:${fin.id}]", ignoreCase = true) == true)
                                }
                                if (!hasPaidThisMonth) fin.installmentAmountCents + (fin.commissionCents ?: 0L) else 0L
                            }

                            val totalScadenzeMese = unpaidRec + unpaidFin
                            val rawAvailable = baseAvailable - totalScadenzeMese

                            // D. Intervento Scudo (se c'è deficit)
                            val deficit = if (rawAvailable < 0L) kotlin.math.abs(rawAvailable) else 0L
                            val scudoIntervento = minOf(deficit, emergencyBalanceCents)
                            val currentAvailable = rawAvailable + scudoIntervento

                            balancesMap[acc.id] = currentAvailable
                        }

                        // 2. Eseguiamo i PAC attingendo al conto corretto
                        for (pac in activePacs) {
                            val pacMatchClassic = Regex("\\[PAC:(\\d+):(\\d+)(?::(\\d+))?\\]").find(pac.name)
                            val pacMatchDyn = Regex("\\[PAC:DYN:([\\d\\-,]+)(?::(\\d+))?\\]").find(pac.name)
                            val accMatch = Regex("\\[ACC:(.*?)\\]").find(pac.name)
                            val targetAccountId = accMatch?.groupValues?.get(1) ?: validAccountsList.firstOrNull()?.id

                            if ((pacMatchClassic != null || pacMatchDyn != null) && targetAccountId != null && balancesMap.containsKey(targetAccountId)) {

                                val availableBalance = balancesMap[targetAccountId] ?: 0L

                                println("🕵️ SPIA PAC: --- INIZIO ANALISI PAC ---")
                                println("🕵️ SPIA PAC: Nome PAC = ${pac.name}")
                                println("🕵️ SPIA PAC: Conto Target = $targetAccountId")
                                println("🕵️ SPIA PAC: Saldo Disponibile del Conto = $availableBalance centesimi (${availableBalance / 100.0} €)")

                                var weeklyCents = 0L
                                var thresholdCents = 0L
                                var executionFrequency = 7

                                if (pacMatchDyn != null) {
                                    val rulesStr = pacMatchDyn.groupValues[1]
                                    val rules = rulesStr.split(",").mapNotNull {
                                        val p = it.split("-")
                                        if (p.size == 2) (p[0].toLongOrNull() ?: 0L) to (p[1].toLongOrNull() ?: 0L) else null
                                    }.sortedByDescending { it.first }
                                    
                                    if (pacMatchDyn.groupValues.size >= 3 && pacMatchDyn.groupValues[2].isNotEmpty()) {
                                        executionFrequency = pacMatchDyn.groupValues[2].toIntOrNull() ?: 7
                                    }

                                    println("🕵️ SPIA PAC: Regole decifrate = $rules")

                                    for ((thr, rate) in rules) {
                                        println("🕵️ SPIA PAC: ---> TESTO SCAGLIONE: Soglia ${thr/100}€ | Rata ${rate/100}€ | Saldo Attuale: ${availableBalance/100.0}€")
                                        if (availableBalance >= thr) {
                                            println("🕵️ SPIA PAC: ✅ SCAGLIONE SUPERATO! Scelgo la rata da ${rate/100}€ (Soglia: ${thr/100}€)")
                                            weeklyCents = rate
                                            thresholdCents = thr
                                            break
                                        } else {
                                            println("🕵️ SPIA PAC: ❌ SCAGLIONE SCARTATO: Saldo ${availableBalance/100.0}€ < Soglia ${thr/100}€")
                                        }
                                    }
                                } else if (pacMatchClassic != null) {
                                    thresholdCents = pacMatchClassic.groupValues[1].toLongOrNull() ?: 0L
                                    weeklyCents = pacMatchClassic.groupValues[2].toLongOrNull() ?: 0L
                                    if (pacMatchClassic.groupValues.size >= 4 && pacMatchClassic.groupValues[3].isNotEmpty()) {
                                        executionFrequency = pacMatchClassic.groupValues[3].toIntOrNull() ?: 7
                                    }
                                }

                                println("🕵️ SPIA PAC: ESITO FINALE -> Rata decisa: ${weeklyCents/100.0}€ | Soglia di blocco: ${thresholdCents/100.0}€ | Frequenza: $executionFrequency")

                                // RIMOSSO IL DOPPIO CONTROLLO SUL SALDO POST-PRELIEVO
                                if (weeklyCents > 0L) {
                                    val pacTransactions = allTransactions.filter {
                                        !it.isDeleted && it.merchantKey?.contains("[pac_sav:${pac.id}]") == true &&
                                                it.description?.contains("Extra", ignoreCase = true) != true &&
                                                (it.type == "TRANSFER" || it.type == "EXPENSE") && it.amountCents > 0
                                    }

                                    val alreadyPaidToday = pacTransactions.any { it.dateEpochDay == currentEpochDay }
                                    val lastPaymentEpoch = pacTransactions.maxOfOrNull { it.dateEpochDay } ?: 0L
                                    val daysSinceLastPayment = currentEpochDay - lastPaymentEpoch

                                    println("🕵️ SPIA PAC: Già pagato oggi? $alreadyPaidToday. Giorni passati: $daysSinceLastPayment (Frequenza richiesta: $executionFrequency)")

                                    if (!alreadyPaidToday && daysSinceLastPayment >= executionFrequency) {
                                        println("🕵️ SPIA PAC: CONDIZIONI SODDISFATTE! 🚀 Genero la transazione...")
                                        val txId = "auto_pac_${pac.id}_${currentEpochDay}"
                                        val cleanName = pac.name.replace(Regex("\\[(PAC|ACC):.*?\\]"), "").trim()

                                        val newTx = TransactionEntity(
                                            id = txId, accountId = targetAccountId, toAccountId = null, fromAccountId = targetAccountId,
                                            amountCents = weeklyCents, type = "TRANSFER",
                                            dateEpochDay = currentEpochDay, categoryId = "cat_savings_auto", description = "Versamento PAC: $cleanName",
                                            merchantKey = "Smart PAC: ${pac.name} [pac_sav:${pac.id}]",
                                            subcategoryText = "Smart PAC", logoEmoji = pac.emoji?.takeIf { it.isNotBlank() } ?: "🤖", logoUri = pac.imageUri ?: "", isHidden = false, isDeleted = false, reminderDays = null,
                                            createdAtEpochMs = nowMs, updatedAtEpochMs = nowMs
                                        )

                                        db.transactionQueries.upsert(
                                            newTx.id, newTx.accountId, newTx.toAccountId, newTx.fromAccountId, newTx.amountCents, newTx.type,
                                            newTx.dateEpochDay, newTx.categoryId, newTx.description, newTx.merchantKey, newTx.subcategoryText,
                                            newTx.logoEmoji, newTx.logoUri, newTx.isHidden, newTx.isDeleted, newTx.reminderDays, newTx.createdAtEpochMs, newTx.updatedAtEpochMs
                                        )
                                        launch { firestore.salva("movimenti", newTx.id, newTx) }

                                        val newSaved = pac.savedAmountCents + weeklyCents
                                        val isCompleted = if (pac.targetAmountCents in 1..newSaved) true else pac.isCompleted

                                        db.savingsGoalQueries.upsert(
                                            pac.id, pac.name, pac.targetAmountCents, newSaved, pac.emoji, pac.imageUri,
                                            pac.isDeleted, isCompleted, pac.createdAtEpochMs, nowMs
                                        )

                                        launch {
                                            val updatedGoal = pac.copy(savedAmountCents = newSaved, isCompleted = isCompleted, updatedAtEpochMs = nowMs)
                                            firestore.salva("risparmi", pac.id, updatedGoal)
                                        }

                                        // 🚀 SCALA I SOLDI PER IL PROSSIMO PAC
                                        balancesMap[targetAccountId] = availableBalance - weeklyCents
                                    }
                                }
                            }
                        }
                    }
                }
            } catch (e: Exception) { println("⚙️ MOTORE PAC ERRORE: ${e.message}") }
        } catch (e: Exception) {
            println("⚙️ MOTORE ERRORE: ${e.message}")
        } finally {
            processMutex.unlock()
        }
    }

    private fun calcolaProssimaScadenza(dataAttuale: LocalDate, giornoTarget: Int, frequency: String?): LocalDate {
        if (frequency != null && frequency.startsWith("CUSTOM_")) {
            val parts = frequency.split("_")
            if (parts.size >= 3) {
                val giorni = parts[1].toIntOrNull() ?: 30
                val saltaWeekend = parts[2] == "SKIP"
                var prossimaData = dataAttuale.plus(DatePeriod(days = giorni))
                if (saltaWeekend) {
                    if (prossimaData.dayOfWeek == DayOfWeek.SATURDAY) prossimaData = prossimaData.plus(DatePeriod(days = 2))
                    else if (prossimaData.dayOfWeek == DayOfWeek.SUNDAY) prossimaData = prossimaData.plus(DatePeriod(days = 1))
                }
                return prossimaData
            }
        }
        var nextMonth = dataAttuale.monthNumber + 1
        var nextYear = dataAttuale.year
        if (nextMonth > 12) { nextMonth = 1; nextYear++ }
        var d = giornoTarget.coerceIn(1, 31)
        while (true) { try { return LocalDate(nextYear, nextMonth, d) } catch (e: Exception) { d-- } }
    }

    private fun addMonths(date: LocalDate, monthsToAdd: Int, preferredDay: Int = date.dayOfMonth): LocalDate {
        var m = date.monthNumber + monthsToAdd
        var y = date.year
        while (m > 12) { m -= 12; y++ }
        return LocalDate(y, m, minOf(preferredDay, lengthOfMonthKmp(y, m)))
    }

    private fun lengthOfMonthKmp(year: Int, month: Int): Int {
        val isLeap = (year % 4 == 0 && (year % 100 != 0 || year % 400 == 0))
        return when (month) { 2 -> if (isLeap) 29 else 28; 4, 6, 9, 11 -> 30; else -> 31 }
    }
}